import React, { Fragment, useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useSignIn, useSignOut } from "react-auth-kit";
import {
  createClientIdQueryString,
  createQueryString,
  sendApiRequest,
} from "../../services/Base/base-service";
import {
  flowTypes,
  notifyUser,
  setFormatHandler,
  showFlowType,
} from "../../services/helpers";

import UserContext from "../../store/user-context";
import {
  Icon,
  Modal,
  ModalBody,
  ModalHeader,
  FormGroup,
  Form,
  Input,
  Toggle,
  Button,
  Alert,
  Spinner,
  Select,
} from "design-react-kit";

import InadServiceType from "../../helpers/Enums/InadServiceType";
import InadAuthParamType from "../../helpers/Enums/InadAuthParamType";

const AuthModuleConfigurationModal = ({
  modalOpen,
  modalCloseHandler,
  selectedClient,
}) => {
  const [inadClients, setInadClients] = useState(null);
  const [selectedInadClient, setSelectedInadClient] = useState(null);
  const [selectedInadServiceType, setSelectedInadServiceType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stagedFlowConfig, setStagedFlowConfig] = useState(null);
  const [format, setFormat] = useState(null);

  const navigate = useNavigate();
  const signIn = useSignIn();
  const signOut = useSignOut();

  const userContext = useContext(UserContext);

  useEffect(() => {
    if (modalOpen) {
      setError(null);
      sendApiRequest(
        "Client/GetAllClientCodesWithInadServiceConfiguration",
        "GET",
        true,
        { fiscalCode: userContext.selectedClientFiscalCode },
        signIn,
        signOut,
        navigate,
        createQueryString,
        userContext,
      ).then((response) => {
        if (response !== undefined) {
          if (response.errors !== null) {
            setError(response.errors);
          } else {
            setError(null);
            let inadclients = [];
            response.data
              .filter((item) => item.clientId !== selectedClient.clientId)
              .map((item) =>
                inadclients.push({
                  value: item.clientId,
                  label: item.codeName,
                }),
              );
            setInadClients(inadclients);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen) {
      setIsLoading(true);
      setError(null);
      setStagedFlowConfig(null);
      setFormat(null);
      setSelectedInadClient(null);
      sendApiRequest(
        "StagedFlowConfiguration/GetSingleStagedFlowConfiguration",
        "GET",
        true,
        { clientId: selectedClient.clientId },
        signIn,
        signOut,
        navigate,
        createClientIdQueryString,
        userContext,
      ).then((response) => {
        if (response !== undefined) {
          if (response.errors !== null) {
            setError(response.errors);
            setIsLoading(false);
          } else {
            setStagedFlowConfig(response.data);
            setFormatHandler(response.data.flowType, setFormat);
            setIsLoading(false);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen) {
      if (stagedFlowConfig !== null) {
        setTimeout(() => {
          const isActiveElement = document.querySelector("input.isActive");
          const isActiveTechElement = document.querySelector(
            "input.hasTechnicalCheck",
          );
          const isActiveEconElement = document.querySelector(
            "input.hasEconomicalCheck",
          );

          if (isActiveElement !== null) {
            isActiveElement.checked = stagedFlowConfig.isActive;
          }

          if (isActiveTechElement !== null) {
            isActiveTechElement.checked = stagedFlowConfig.hasTechnicalCheck;
          }

          if (isActiveEconElement !== null) {
            isActiveEconElement.checked = stagedFlowConfig.hasEconomicalCheck;
          }
        }, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, stagedFlowConfig]);

  useEffect(() => {
    if (
      modalOpen &&
      stagedFlowConfig?.inadServiceConfiguration !== undefined &&
      stagedFlowConfig?.inadServiceConfiguration?.inadServiceType !== undefined
    ) {
      const matchingService = inadServiceTypeOptions.find(
        (service) =>
          service.value ===
          stagedFlowConfig.inadServiceConfiguration.inadServiceType,
      );
      setSelectedInadServiceType(matchingService ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedFlowConfig]);

  const inadServiceTypeOptions = Object.entries(InadServiceType).map(
    ([key, value]) => ({
      label: key,
      value: value,
    }),
  );

  const paramTypeOptions = Object.entries(InadAuthParamType).map(
    ([key, value]) => ({
      label: key,
      value: value,
    }),
  );

  const upsertClientConfiguration = (e) => {
    e.stopPropagation();
    setError(null);
    let hasError = false;

    let data = stagedFlowConfig;
    if (stagedFlowConfig.clientId === null || stagedFlowConfig.clientId === 0) {
      data = { ...data, clientId: selectedClient.clientId };
    }

    if (
      data.documentsAvailabilityDays === null ||
      data.documentsAvailabilityDays === undefined ||
      data.documentsAvailabilityDays === ""
    ) {
      hasError = true;
      setError((prevState) =>
        prevState !== null
          ? [
              ...prevState,
              `Il valore dei "Giorni di permanenza PDF già trasmessi a EN CORE" è obbligatorio`,
            ]
          : [
              `Il valore dei "Giorni di permanenza PDF già trasmessi a EN CORE" è obbligatorio`,
            ],
      );
    }

    if (
      data.digitalCoefficient === null ||
      data.digitalCoefficient === undefined ||
      data.digitalCoefficient === ""
    ) {
      hasError = true;
      setError((prevState) =>
        prevState !== null
          ? [
              ...prevState,
              `Il valore dei "Coeff. Moltiplicativo (costo piattaforma)" è obbligatorio`,
            ]
          : [
              `Il valore dei "Coeff. Moltiplicativo (costo piattaforma)" è obbligatorio`,
            ],
      );
    }

    if (
      data.analogicalCoefficient === null ||
      data.analogicalCoefficient === undefined ||
      data.analogicalCoefficient === ""
    ) {
      hasError = true;
      setError((prevState) =>
        prevState !== null
          ? [
              ...prevState,
              `Il valore dei "Coeff. Moltiplicativo (costi notifiche analogiche)" è obbligatorio`,
            ]
          : [
              `Il valore dei "Coeff. Moltiplicativo (costi notifiche analogiche)" è obbligatorio`,
            ],
      );
    }

    var fiscalCodeRegex = new RegExp(
      /^\d{11}|[a-zA-Z]{6}\d{2}[A-Za-z]{1}\d{2}[a-zA-Z]{1}\d{3}[a-zA-Z]{1}$/,
    );

    if (
      data.inadServiceConfiguration.inadServiceType ===
        InadServiceType.Genova &&
      !fiscalCodeRegex.test(data.inadServiceConfiguration.operatorFiscalCode)
    ) {
      hasError = true;
      var fiscalCodeErrorMessage =
        "Il codice fiscale dell'operatore deve identificare una persona fisica (reale codice fiscale di 16 caratteri) oppure identificare l'Ente che esegue l'interrogazione verso INAD (partita IVA o codice fiscale numerico di 11 cifre)";
      setError((prevState) =>
        prevState !== null
          ? [...prevState, fiscalCodeErrorMessage]
          : [fiscalCodeErrorMessage],
      );
    }

    if (hasError) {
      const modalBody = document.querySelector(".modal.show .modal-body");
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    }

    if (!hasError) {
      setIsLoading(true);
      sendApiRequest(
        `StagedFlowConfiguration/UpsertStagedFlowConfiguration`,
        "POST",
        true,
        { ...data },
        signIn,
        signOut,
        navigate,
        null,
        userContext,
      ).then((response) => {
        if (response !== undefined) {
          if (response.errors !== null) {
            setError(response.errors);
            setIsLoading(false);
          } else {
            modalCloseHandler();
            notifyUser(
              "Aggiornamento configurazione per tipologia servizio completato con successo!",
              "success",
              "it-check",
            );
          }
        }
      });
    }
  };

  const technicalCheckHandler = (isChecked) => {
    if (!isChecked && !stagedFlowConfig.hasEconomicalCheck) {
      setStagedFlowConfig((curState) => {
        return {
          ...curState,
          hasTechnicalCheck: isChecked,
          isActive: false,
        };
      });
    } else {
      setStagedFlowConfig((curState) => {
        return {
          ...curState,
          hasTechnicalCheck: isChecked,
        };
      });
    }
  };

  const economicalCheckHandler = (isChecked) => {
    if (!isChecked && !stagedFlowConfig.hasTechnicalCheck) {
      setStagedFlowConfig((curState) => {
        return {
          ...curState,
          hasEconomicalCheck: isChecked,
          isActive: false,
        };
      });
    } else {
      setStagedFlowConfig((curState) => {
        return {
          ...curState,
          hasEconomicalCheck: isChecked,
        };
      });
    }
  };

  const handleFormatChange = (selectedOption) => {
    setFormat(selectedOption);
    setStagedFlowConfig((curState) => {
      return {
        ...curState,
        flowType: selectedOption.value,
      };
    });
  };

  const handleInadClientChange = (selectedOption) => {
    setSelectedInadClient(selectedOption);
    handleCopyClient(selectedOption);
  };

  const handleCopyClient = (selectedOption) => {
    if (selectedOption) {
      setError(null);
      sendApiRequest(
        "Inad/GetSingleStagedFlowInadConfiguration",
        "GET",
        true,
        { clientId: selectedOption.value },
        signIn,
        signOut,
        navigate,
        createClientIdQueryString,
        userContext,
      ).then((response) => {
        if (response !== undefined) {
          if (response.errors !== null) {
            setError(response.errors);
          } else {
            setError(null);
            updateInadInfoData(response.data);
          }
        }
      });
    }
  };

  const updateInadInfoData = (inadInfoData) => {
    if (inadInfoData) {
      setStagedFlowConfig((curState) => ({
        ...curState,
        inadServiceConfiguration: {
          inadServiceType: inadInfoData.inadServiceType,
          bearerToken: inadInfoData.bearerToken,
          authUrl: inadInfoData.authUrl,
          inadAuthParamInfos: inadInfoData.inadAuthParamInfos,
        },
      }));
    } else {
      setStagedFlowConfig((curState) => ({
        ...curState,
        inadServiceConfiguration: {
          inadServiceType: null,
          bearerToken: null,
          authUrl: null,
          inadAuthParamInfos: [],
        },
      }));
    }
  };

  const handleInadServiceChange = (selectedOption) => {
    setSelectedInadServiceType(selectedOption);
    updateInadInfoData(null);
    setStagedFlowConfig((curState) => ({
      ...curState,
      inadServiceConfiguration: {
        ...(curState.inadServiceConfiguration ?? {}),
        inadServiceType: selectedOption?.value ?? null,
      },
    }));
  };

  const updateAuthParam = (index, field, value) => {
    const updatedParams = [
      ...(stagedFlowConfig?.inadServiceConfiguration?.inadAuthParamInfos ?? []),
    ];
    updatedParams[index] = { ...updatedParams[index], [field]: value };

    setStagedFlowConfig((curState) => ({
      ...curState,
      inadServiceConfiguration: {
        ...(curState.inadServiceConfiguration ?? {}),
        inadAuthParamInfos: updatedParams,
      },
    }));
  };

  const addAuthParam = () => {
    const updatedParams = [
      ...(stagedFlowConfig?.inadServiceConfiguration?.inadAuthParamInfos ?? []),
    ];
    updatedParams.push({ name: "", value: "", inadAuthParamType: 0 });

    setStagedFlowConfig((curState) => ({
      ...curState,
      inadServiceConfiguration: {
        ...(curState.inadServiceConfiguration ?? {}),
        inadAuthParamInfos: updatedParams,
      },
    }));
  };

  const removeAuthParam = (index) => {
    const updatedParams = [
      ...(stagedFlowConfig?.inadServiceConfiguration?.inadAuthParamInfos ?? []),
    ];
    updatedParams.splice(index, 1);

    setStagedFlowConfig((curState) => ({
      ...curState,
      inadServiceConfiguration: {
        ...(curState.inadServiceConfiguration ?? {}),
        inadAuthParamInfos: updatedParams,
      },
    }));
  };

  return (
    <Modal
      isOpen={modalOpen}
      toggle={modalCloseHandler}
      align="center"
      scrollable
      labelledBy="authModuleConfig"
    >
      <ModalHeader
        toggle={modalCloseHandler}
        id="authModuleConfig"
        className="mb-4"
      >
        <h5 className="text-capitalize">
          Tipologia Servizio:{" "}
          <span style={{ color: "#0073e6 " }}>
            {selectedClient?.clientCode}
          </span>
        </h5>
        <h6 className="text-capitalize" style={{ color: "#0073e6 " }}>
          {selectedClient?.clientCodeName}
        </h6>
      </ModalHeader>
      <ModalBody>
        {isLoading && <Spinner double active />}
        {!isLoading && error !== null && error.length !== undefined && (
          <Alert color="danger">
            {error.map((item) => (
              <p className="mb-3" key={item}>
                {item}
              </p>
            ))}
          </Alert>
        )}
        {stagedFlowConfig !== null && stagedFlowConfig !== undefined && (
          <Form className="mr-auto ml-auto">
            {inadClients !== null && inadClients.length > 0 && (
              <>
                <div className="d-flex align-items-end gap-1 mb-5 ml-1">
                  <div className="flex-grow-1 bootstrap-select-wrapper">
                    <label htmlFor="clientInadServiceTypes">
                      Copia da altra configurazione gateway servizi INAD
                    </label>
                    <Select
                      id="clientInadServiceTypes"
                      placeholder="Seleziona la tipologia di servizio da cui copiare"
                      aria-label="Seleziona"
                      value={selectedInadClient}
                      onChange={handleInadClientChange}
                      options={inadClients}
                    />
                  </div>
                </div>
              </>
            )}
            <Fragment>
              <div className="bootstrap-select-wrapper mb-5 col-sm-12">
                <label htmlFor="inadServiceTypes">Gateway servizi INAD</label>
                <Select
                  isSearchable={false}
                  id="inadServiceTypes"
                  placeholder="Selezione il gateway di interfacciamento ai servizi INAD"
                  aria-label="Seleziona"
                  value={selectedInadServiceType}
                  onChange={handleInadServiceChange}
                  options={inadServiceTypeOptions}
                />
              </div>
              {selectedInadServiceType && (
                <>
                  {selectedInadServiceType?.value ===
                  InadServiceType.EasySynergy ? (
                    <FormGroup className="col-sm-12 m-0 position-relative mb-5">
                      <Input
                        id="bearerToken"
                        type="text"
                        label="API Key"
                        placeholder="Inserisci l' API Key fornita da EasySynergy"
                        value={
                          stagedFlowConfig?.inadServiceConfiguration
                            ?.bearerToken ?? ""
                        }
                        onChange={(e) =>
                          setStagedFlowConfig((curState) => ({
                            ...curState,
                            inadServiceConfiguration: {
                              ...curState.inadServiceConfiguration,
                              bearerToken: e.target.value,
                            },
                          }))
                        }
                        required
                      />
                    </FormGroup>
                  ) : (
                    <>
                      <FormGroup className="col-sm-12 m-0 position-relative mb-5">
                        <Input
                          id="operatorFiscalCode"
                          type="text"
                          label="Codice fiscale operatore"
                          placeholder="Inserisci Codice fiscale operatore"
                          value={
                            stagedFlowConfig?.inadServiceConfiguration
                              ?.operatorFiscalCode ?? ""
                          }
                          onChange={(e) =>
                            setStagedFlowConfig((curState) => ({
                              ...curState,
                              inadServiceConfiguration: {
                                ...curState.inadServiceConfiguration,
                                operatorFiscalCode: e.target.value,
                              },
                            }))
                          }
                          required
                        />
                      </FormGroup>
                      <FormGroup className="col-sm-12 m-0 mb-5 position-relative">
                        <Input
                          id="authUrl"
                          type="text"
                          label="URL del servizio autenticazione"
                          placeholder="Inserisci l' URL del servizio autenticazione"
                          value={
                            stagedFlowConfig?.inadServiceConfiguration
                              ?.authUrl ?? ""
                          }
                          onChange={(e) =>
                            setStagedFlowConfig((curState) => ({
                              ...curState,
                              inadServiceConfiguration: {
                                ...curState.inadServiceConfiguration,
                                authUrl: e.target.value,
                              },
                            }))
                          }
                          required
                        />
                      </FormGroup>

                      {/* Dynamic Auth Params */}
                      <div className="bootstrap-select-wrapper mt-5 mb-5 col-sm-12">
                        <label className="mt-3 mb-4">
                          Parametri da trasmettere al servizio di autenticazione
                        </label>
                        {(
                          stagedFlowConfig?.inadServiceConfiguration
                            ?.inadAuthParamInfos ?? []
                        ).map((param, index) => (
                          <div key={index} className="form-row mb-2">
                            <div className="col-sm-3">
                              <Input
                                type="text"
                                value={param.name}
                                placeholder="Nome"
                                onChange={(e) =>
                                  updateAuthParam(index, "name", e.target.value)
                                }
                                required
                              />
                            </div>
                            <div className="col-sm-3">
                              <Input
                                type="text"
                                value={param.value}
                                placeholder="Valore"
                                onChange={(e) =>
                                  updateAuthParam(
                                    index,
                                    "value",
                                    e.target.value,
                                  )
                                }
                                required
                              />
                            </div>
                            <div className="col-sm-3">
                              <Select
                                isSearchable={false}
                                options={paramTypeOptions}
                                value={paramTypeOptions.find(
                                  (opt) =>
                                    opt.value === param.inadAuthParamType,
                                )}
                                onChange={(selected) =>
                                  updateAuthParam(
                                    index,
                                    "inadAuthParamType",
                                    selected.value,
                                  )
                                }
                              />
                            </div>
                            <div className="col-sm-3 d-flex justify-content-center align-items-center">
                              <span title="Rimuovi il parametro">
                                <Icon
                                  id="delete-auth-param"
                                  icon="it-delete"
                                  fill="#005A9A"
                                  role="button"
                                  onClick={(e) => removeAuthParam(index)}
                                />
                              </span>
                            </div>
                          </div>
                        ))}
                        <div className="bootstrap-select-wrapper mb-2 d-flex justify-content-center align-items-center">
                          <span title="Aggiungi nuovo parametro">
                            <Icon
                              id="add-auth-param"
                              icon="it-plus-circle"
                              fill="#005A9A"
                              role="button"
                              onClick={(e) => addAuthParam()}
                            />
                          </span>
                        </div>
                        <hr className="mx-auto" />
                      </div>
                    </>
                  )}
                </>
              )}
            </Fragment>

            {stagedFlowConfig.flowType !== null &&
              stagedFlowConfig.flowType !== undefined && (
                <div className="p-input-wrapper">
                  <div className="p-label" htmlFor="secretKey">
                    Formato
                  </div>
                  <p id="secretKey" className="p-input">
                    {showFlowType(stagedFlowConfig.flowType)}
                  </p>
                </div>
              )}
            {(stagedFlowConfig.flowType === null ||
              stagedFlowConfig.flowType === undefined) && (
              <div className="bootstrap-select-wrapper mb-5">
                <label htmlFor="format">Formato</label>
                <Select
                  id="format"
                  placeholder="Inserisci formato"
                  aria-label="Inserisci formato"
                  value={format}
                  onChange={handleFormatChange}
                  options={flowTypes}
                />
              </div>
            )}
            <Fragment>
              <div className="form-row">
                <FormGroup className="col-sm-12 m-0 position-relative">
                  <Input
                    id="documentAvailabilityDays"
                    type="number"
                    min={0}
                    label="Giorni di permanenza PDF già trasmessi a EN CORE"
                    placeholder="Inserisci Giorni di permanenza PDF già trasmessi a EN CORE"
                    value={stagedFlowConfig.documentsAvailabilityDays}
                    onChange={(e) =>
                      setStagedFlowConfig((curState) => {
                        return {
                          ...curState,
                          documentsAvailabilityDays: e.target.value,
                        };
                      })
                    }
                  />
                </FormGroup>
              </div>
              <div className="form-row">
                <FormGroup className="col-sm-12 m-0 position-relative">
                  <Input
                    id="digitalCoefficient"
                    type="text"
                    min={0}
                    label="Coeff. Moltiplicativo (costo piattaforma)"
                    placeholder="Inserisci Coeff. Moltiplicativo (costo piattaforma)"
                    value={stagedFlowConfig.digitalCoefficient}
                    onChange={(e) =>
                      setStagedFlowConfig((curState) => {
                        return {
                          ...curState,
                          digitalCoefficient: e.target.value,
                        };
                      })
                    }
                  />
                </FormGroup>
              </div>
              <div className="form-row">
                <FormGroup className="col-sm-12 m-0 position-relative">
                  <Input
                    id="analogicalCoefficient"
                    type="text"
                    min={0}
                    label="Coeff. Moltiplicativo (costi notifiche analogiche)"
                    placeholder="Inserisci Coeff. Moltiplicativo (costi notifiche analogiche)"
                    value={stagedFlowConfig.analogicalCoefficient}
                    onChange={(e) =>
                      setStagedFlowConfig((curState) => {
                        return {
                          ...curState,
                          analogicalCoefficient: e.target.value,
                        };
                      })
                    }
                  />
                </FormGroup>
              </div>

              <FormGroup
                check
                className="form-check-group d-flex align-items-center justify-content-start mb-3 isActive"
              >
                <Toggle
                  id="isActive-toggle"
                  className="hasTechnicalCheck"
                  onClick={(e) => technicalCheckHandler(e.target.checked)}
                />
                <span className="mb-2 toggles-span" id="hasTechnicalCheck">
                  Autorizzazione tecnica
                </span>
              </FormGroup>
              <FormGroup
                check
                className="form-check-group d-flex align-items-center justify-content-start mb-3 isActive"
              >
                <Toggle
                  id="isActive-toggle"
                  className="hasEconomicalCheck"
                  onClick={(e) => economicalCheckHandler(e.target.checked)}
                />
                <span className="mb-2 toggles-span" id="hasEconomicalCheck">
                  Autorizzazione finanziaria
                </span>
              </FormGroup>
              {(stagedFlowConfig.hasEconomicalCheck ||
                stagedFlowConfig.hasTechnicalCheck) && (
                <FormGroup
                  check
                  className="form-check-group d-flex align-items-center justify-content-start mb-3 isActive"
                >
                  <Toggle
                    id="isActive-toggle"
                    className="isActive"
                    onClick={(e) =>
                      setStagedFlowConfig((curState) => {
                        return {
                          ...curState,
                          isActive: e.target.checked,
                        };
                      })
                    }
                  />
                  <span className="mb-2 toggles-span" id="isActive">
                    Configurazione abilitata
                  </span>
                </FormGroup>
              )}
            </Fragment>

            <FormGroup className="col-sm-12 mb-2 d-flex align-items-center justify-content-end">
              <Button
                id="user-cancel"
                color="white"
                className="mr-2"
                onClick={modalCloseHandler}
              >
                Annulla
              </Button>
              <Button
                style={{
                  backgroundColor: "#5EB423",
                  color: "#fff",
                  padding: "7px 15px",
                }}
                id="user-save"
                disabled={
                  !stagedFlowConfig.hasEconomicalCheck &&
                  !stagedFlowConfig.hasTechnicalCheck
                }
                onClick={(e) => upsertClientConfiguration(e)}
              >
                <Icon
                  icon="it-check"
                  fill="#ffffff"
                  onClick={(e) => upsertClientConfiguration(e)}
                />
                Salva
              </Button>
            </FormGroup>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};

export default AuthModuleConfigurationModal;
